import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminNormative } from "../../../../../http/vertigrip/types/normatives";
import { useNormativesApi } from "../../../../../http/vertigrip/useNormatives";
import { PaginationQuery } from "../../../../../types";
import Button from "../../../../ui/Button";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import NewNormative from "./NewNormative";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const Normatives: FC<Props> = ({ active }) => {
  const { systemId } = useParams<{ systemId: string }>();
  const i18n = useI18n();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "normatives" });
  const aborter = useRef(new AbortController());
  const normativesApi = useNormativesApi();
  const { setActions, setTitle } = useSearchContext();

  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<AdminNormative>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminNormative>("vertigrip.normative");
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "normatives");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = normativesApi.useAdminNormatives(query, aborter.current.signal);

  const updateVisibility = async (normativeId: string, value: VisibilityScopeValue) => {
    try {
      await normativesApi.updateAdminNormativeVisibility(normativeId, { visibility: value });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (normativeId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await normativesApi.updateAdminNormativeOrder(normativeId, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.normatives.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.normatives.search} value={searchTerm} onChange={handleSearchChange} />
        <Button buttonProps={{ onClick: () => setShowNewItemDialog(true) }}>{i18n.translation.common.createNew}</Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminNormative>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/vertigrip_normatives/${item.id}`}
              >
                {item.name}
              </Link>
            </td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => navigate(`/admin/systems/${systemId}/vertigrip_normatives/${item.id}`)}
        rowActions={(item) => [
          getEditAction(i18n, () => navigate(`/admin/systems/${systemId}/vertigrip_normatives/${item?.id}`)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {showNewItemDialog && (
        <ModalDialog onClose={() => setShowNewItemDialog(false)} title={i18n.translation.normatives.createNew}>
          <NewNormative />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.normatives.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                normativesApi
                  .deleteAdminNormative(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.normatives.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Normatives;
